/**
 * Routing über offene Dienste.
 *
 * Auto/Velo/zu Fuss: Valhalla auf dem FOSSGIS-Server der OSM-Community.
 *   Für den Dauerbetrieb auf osm.kgva.ch gehört dort eine eigene Instanz hin
 *   (oder ein Dienst mit Vertrag) – die öffentliche ist für kleine Last gedacht.
 * ÖV: transport.opendata.ch, die offene Schnittstelle auf die Fahrplandaten
 *   der SBB und der übrigen Schweizer Transportunternehmen.
 */
import { haltestelleBei } from './oev.js';

const VALHALLA = 'https://valhalla1.openstreetmap.de/route';
const VALHALLA_TRACE = 'https://valhalla1.openstreetmap.de/trace_attributes';
const VALHALLA_HEIGHT = 'https://valhalla1.openstreetmap.de/height';
const TRANSPORT = 'https://transport.opendata.ch/v1';

export const MODES = [
  { id: 'auto', label: 'Auto', costing: 'auto' },
  { id: 'bicycle', label: 'Velo', costing: 'bicycle' },
  { id: 'pedestrian', label: 'Zu Fuss', costing: 'pedestrian' },
  { id: 'transit', label: 'ÖV', costing: null }
];

/**
 * Velotypen, wie Valhalla sie kennt - mit dem, was sie tatsächlich bewirken.
 *
 * `bicycle_type` setzt nicht nur das Tempo, sondern auch, welche Beläge als
 * schlecht gelten: ein Rennvelo meidet Kies, ein Mountainbike nicht. Die
 * Reisegeschwindigkeit ist Valhallas Standardwert je Typ, hier nur
 * ausgeschrieben, damit sie in der Spalte steht und nicht geraten werden muss.
 */
export const VELOTYPEN = [
  { id: 'City', label: 'Stadtvelo', tempo: 18 },
  { id: 'Hybrid', label: 'Alltagsvelo', tempo: 22 },
  { id: 'Road', label: 'Rennvelo', tempo: 25 },
  { id: 'Cross', label: 'Gravel', tempo: 22 },
  { id: 'Mountain', label: 'Mountainbike', tempo: 18 }
];

/**
 * Was der Mensch einstellt -> was Valhalla versteht.
 *
 * Die drei Regler laufen bei Valhalla von 0 bis 1 und sind für sich genommen
 * nicht sprechend. `use_hills` 0 heisst "Steigungen meiden", nicht "keine
 * Hügel"; `use_roads` 0 heisst "lieber Nebenstrasse und Veloweg als
 * Hauptstrasse". Der Standardwert ist bei beiden 0,5 - also genau dazwischen.
 */
export function veloOptionen({ typ = 'Hybrid', huegel = false, verkehr = false, belag = false } = {}) {
  const t = VELOTYPEN.find((x) => x.id === typ) || VELOTYPEN[1];
  return {
    bicycle_type: t.id,
    cycling_speed: t.tempo,
    // 0 ist Valhallas Randwert "Steigungen meiden, auch für einen Umweg".
    // Gemessen an drei hügeligen Strecken mit echter Alternative brachte das
    // 5 bis 17 Höhenmeter weniger und nie mehr - wo nur eine Strasse
    // hinaufführt, ändert sich erwartungsgemäss nichts.
    use_hills: huegel ? 0 : 0.5,
    use_roads: verkehr ? 0.15 : 0.5,
    // Standard ist 0,25 - das meidet schlechte Beläge kaum. Wer es ankreuzt,
    // will wirklich nicht auf Kies.
    avoid_bad_surfaces: belag ? 0.9 : 0.25
  };
}

/** Valhalla liefert die Geometrie als Polyline mit sechs Nachkommastellen. */
export function decodePolyline(str, precision = 6) {
  const factor = 10 ** precision;
  const coords = [];
  let index = 0, lat = 0, lng = 0;

  while (index < str.length) {
    let shift = 0, result = 0, byte;
    do {
      byte = str.charCodeAt(index++) - 63;
      result |= (byte & 0x1f) << shift;
      shift += 5;
    } while (byte >= 0x20);
    lat += result & 1 ? ~(result >> 1) : result >> 1;

    shift = 0;
    result = 0;
    do {
      byte = str.charCodeAt(index++) - 63;
      result |= (byte & 0x1f) << shift;
      shift += 5;
    } while (byte >= 0x20);
    lng += result & 1 ? ~(result >> 1) : result >> 1;

    coords.push([lng / factor, lat / factor]);
  }
  return coords;
}

/**
 * `via` setzt einen Zwischenhalt - etwa eine Tankstelle unterwegs. Valhalla
 * nimmt beliebig viele Orte; wir brauchen genau einen dazwischen.
 */
export async function routeVehicle({ from, to, via, costing, velo, varianten = 0, signal }) {
  const res = await fetch(VALHALLA, {
    method: 'POST',
    signal,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      locations: [
        { lat: from.lat, lon: from.lng },
        ...(via ? [{ lat: via.lat, lon: via.lng, type: 'through' }] : []),
        { lat: to.lat, lon: to.lng }
      ],
      costing,
      costing_options: costing === 'bicycle' ? { bicycle: veloOptionen(velo) } : undefined,
      directions_options: { language: 'de-DE', units: 'kilometers' },
      alternates: varianten || undefined
    })
  });
  // Valhalla antwortet mit 400, wenn es zwischen den Punkten keinen Weg kennt.
  if (res.status === 400) throw new Error('Zwischen diesen Punkten ist kein Weg bekannt.');
  if (!res.ok) throw new Error(`Routing nicht möglich (${res.status})`);
  const data = await res.json();
  const trip = data.trip;
  if (!trip || trip.status !== 0) throw new Error(trip?.status_message || 'Keine Route gefunden.');

  const haupt = fahrtLesen(trip, costing);
  // Varianten stehen in einer eigenen Liste, jede mit demselben Aufbau wie
  // die Hauptroute. Valhalla gibt hoechstens zwei zurueck, auch wenn man mehr
  // verlangt - gemessen auf der eigenen Instanz.
  const andere = (data.alternates || [])
    .map((a) => (a.trip && a.trip.status === 0 ? fahrtLesen(a.trip, costing) : null))
    .filter(Boolean);
  return andere.length ? { ...haupt, varianten: andere } : haupt;
}

/** Eine Valhalla-Fahrt in die Form bringen, mit der die App arbeitet. */
function fahrtLesen(trip, costing) {
  const coordinates = trip.legs.flatMap((leg) => decodePolyline(leg.shape));
  const steps = trip.legs.flatMap((leg) =>
    leg.maneuvers.map((m) => ({ text: m.instruction, km: m.length }))
  );

  // Fuer die Fuehrung braucht es mehr als den Text: die Punktnummern auf der
  // Linie, die Kompasswinkel und die fertigen Sprachansagen, die Valhalla auf
  // Deutsch mitliefert. Bei mehreren Beinen die Punktnummern verschieben.
  const maneuvers = [];
  let versatz = 0;
  for (const leg of trip.legs) {
    for (const m of leg.maneuvers) {
      maneuvers.push({
        typ: m.type,
        text: m.instruction,
        ansageVoraus: m.verbal_transition_alert_instruction || m.instruction,
        ansageJetzt: m.verbal_pre_transition_instruction || m.verbal_succinct_transition_instruction || m.instruction,
        ansageDanach: m.verbal_post_transition_instruction || '',
        strasse: (m.street_names || [])[0] || '',
        von: m.begin_shape_index + versatz,
        bis: m.end_shape_index + versatz,
        kurs: m.bearing_after,
        km: m.length,
        sekunden: m.time
      });
    }
    versatz += decodePolyline(leg.shape).length;
  }

  return {
    kind: 'vehicle',
    coordinates,
    distanceKm: trip.summary.length,
    durationMin: Math.round(trip.summary.time / 60),
    steps,
    maneuvers,
    // Fuer die Tempolimit-Abfrage - nur das erste Bein, mehr gibt es bei uns nicht.
    shape: trip.legs[0]?.shape || null,
    costing
  };
}

/**
 * Fussweg zwischen zwei Halten.
 *
 * Der Fahrplan liefert für Umstiege und Zubringer nur Anfang und Ende. Eine
 * Gerade dazwischen führt über Gleise und durch Häuser; Valhalla kennt die
 * Wege. Scheitert die Abfrage, kommt `null` - dann bleibt der Abschnitt
 * ungezeichnet statt falsch gezeichnet.
 */
export async function routeFootpath({ from, to, signal }) {
  // Umstieg im selben Bahnhof: zwei Punkte auf demselben Fleck, kein Weg.
  if (Math.abs(from.lat - to.lat) < 1e-5 && Math.abs(from.lng - to.lng) < 1e-5) return null;
  try {
    const res = await fetch(VALHALLA, {
      method: 'POST',
      signal,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        locations: [{ lat: from.lat, lon: from.lng }, { lat: to.lat, lon: to.lng }],
        costing: 'pedestrian',
        directions_options: { language: 'de-DE', units: 'kilometers' }
      })
    });
    if (!res.ok) return null;
    const trip = (await res.json()).trip;
    if (!trip || trip.status !== 0) return null;
    return trip.legs.flatMap((leg) => decodePolyline(leg.shape));
  } catch (e) {
    if (e.name === 'AbortError') throw e;
    return null;
  }
}

/**
 * Tempolimits je Streckenabschnitt. Das Routing selbst liefert sie nicht;
 * `trace_attributes` wertet dieselbe Linie nochmals aus und gibt sie mit den
 * Punktnummern zurueck. Gemessen: 96 % der Kanten tragen ein Limit, rund
 * 11 KB Antwort fuer acht Kilometer.
 */
export async function fetchSpeedLimits({ shape, costing, signal }) {
  if (!shape) return [];
  const res = await fetch(VALHALLA_TRACE, {
    method: 'POST',
    signal,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      encoded_polyline: shape,
      costing,
      shape_match: 'edge_walk',
      filters: {
        attributes: ['edge.speed_limit', 'edge.begin_shape_index', 'edge.end_shape_index'],
        action: 'include'
      }
    })
  });
  if (!res.ok) return [];
  const d = await res.json();
  return (d.edges || [])
    .filter((e) => e.speed_limit)
    .map((e) => ({ von: e.begin_shape_index, bis: e.end_shape_index, limit: e.speed_limit }));
}

/**
 * Höhenprofil entlang der Route.
 *
 * Valhalla gibt zu einer Linie die Höhe jedes Stützpunkts samt seiner
 * Entfernung vom Anfang (`range_height`): eine Liste von [Meter, Höhe].
 * Gemessen rund 320 Punkte auf vier Kilometer.
 */
/** Meter, ab denen eine Höhenänderung als echt gilt und nicht als Rauschen. */
const SCHWELLE = 3;

export async function fetchHoehenprofil({ shape, signal }) {
  if (!shape) return null;
  try {
    const res = await fetch(VALHALLA_HEIGHT, {
      method: 'POST',
      signal,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ encoded_polyline: shape, range: true, shape_format: 'polyline6' })
    });
    if (!res.ok) return null;
    const d = await res.json();
    const roh = (d.range_height || []).filter((x) => Array.isArray(x) && Number.isFinite(x[1]));
    if (roh.length < 2) return null;
    const punkte = roh.map(([m, h]) => ({ m, h }));

    // Auf- und Abstieg mit Schwelle, nicht Punkt für Punkt: das Höhenmodell
    // rauscht um ein, zwei Meter, und über dreihundert Stützpunkte summiert
    // sich das zu Höhenmetern, die niemand fährt. Gezählt wird erst, wenn die
    // Höhe sich um mehr als die Schwelle von der letzten bestätigten
    // unterscheidet - dieselbe Hysterese wie bei den Touren.
    let aufstieg = 0;
    let abstieg = 0;
    let anker = punkte[0].h;
    for (const p of punkte) {
      const d2 = p.h - anker;
      if (d2 >= SCHWELLE) { aufstieg += d2; anker = p.h; }
      else if (d2 <= -SCHWELLE) { abstieg += -d2; anker = p.h; }
    }
    const hoehen = punkte.map((x) => x.h);
    return {
      punkte,
      laengeM: punkte[punkte.length - 1].m,
      aufstieg: Math.round(aufstieg),
      abstieg: Math.round(abstieg),
      min: Math.min(...hoehen),
      max: Math.max(...hoehen)
    };
  } catch {
    return null;
  }
}

/* ------------------------------------------------- Das Profil auswerten */

/**
 * Die Anstiege einer Route.
 *
 * Ein Höhenprofil zeigt, dass es aufwärts geht - aber nicht, wo der Anstieg
 * anfängt, wie lang er ist und wie steil. Genau das will wissen, wer fährt:
 * "gleich 1,2 Kilometer mit 6 Prozent" lässt sich einteilen, eine Zackenlinie
 * nicht.
 *
 * Gesucht wird wie bei einem Bergpreis: von einem Tiefpunkt aus aufwärts,
 * solange es nicht mehr als `TOLERANZ` unter den höchsten bisher erreichten
 * Punkt zurückfällt. Kleine Senken innerhalb eines Anstiegs zerschneiden ihn
 * damit nicht - wer eine Passstrasse fährt, erlebt die flache Kehre als Teil
 * des Anstiegs und nicht als dessen Ende.
 */
const TOLERANZ = 10;        // Meter Rückfall, die ein Anstieg verträgt
const MIN_GEWINN = 20;      // darunter ist es eine Welle, kein Anstieg
const MIN_STEIGUNG = 3;     // Prozent im Mittel

export function anstiege(profil) {
  const punkte = profil?.punkte;
  if (!punkte || punkte.length < 3) return [];
  const gefunden = [];
  let tief = punkte[0];
  let gipfel = punkte[0];

  const abschliessen = () => {
    const gewinn = gipfel.h - tief.h;
    const laenge = gipfel.m - tief.m;
    if (gewinn >= MIN_GEWINN && laenge > 0) {
      const steigung = (gewinn / laenge) * 100;
      if (steigung >= MIN_STEIGUNG) {
        gefunden.push({
          von: tief.m,
          bis: gipfel.m,
          laenge,
          gewinn: Math.round(gewinn),
          steigung: Math.round(steigung * 10) / 10
        });
      }
    }
  };

  for (const p of punkte) {
    if (p.h >= gipfel.h) { gipfel = p; continue; }
    if (gipfel.h - p.h > TOLERANZ) {
      abschliessen();
      tief = p;
      gipfel = p;
    }
  }
  abschliessen();
  return gefunden;
}

/**
 * Steigung an einer Stelle, in Prozent.
 *
 * Über ein Fenster gemittelt und nicht aus zwei benachbarten Stützpunkten:
 * das Höhenmodell rauscht, und zwischen zwei Punkten im Abstand von dreissig
 * Metern ergäbe ein Meter Rauschen schon 3 Prozent.
 */
export function steigungBei(profil, meter, fenster = 150) {
  const punkte = profil?.punkte;
  if (!punkte || punkte.length < 2) return null;
  const a = hoeheBei(punkte, meter - fenster / 2);
  const b = hoeheBei(punkte, meter + fenster / 2);
  if (a === null || b === null) return null;
  const spanne = Math.min(punkte[punkte.length - 1].m, meter + fenster / 2)
    - Math.max(0, meter - fenster / 2);
  if (spanne < 20) return null;
  return Math.round(((b - a) / spanne) * 1000) / 10;
}

/** Höhe an einer beliebigen Stelle, zwischen den Stützpunkten interpoliert. */
function hoeheBei(punkte, meter) {
  const m = Math.max(punkte[0].m, Math.min(punkte[punkte.length - 1].m, meter));
  let i = 1;
  while (i < punkte.length - 1 && punkte[i].m < m) i++;
  const a = punkte[i - 1];
  const b = punkte[i];
  if (b.m === a.m) return a.h;
  return a.h + ((b.h - a.h) * (m - a.m)) / (b.m - a.m);
}

/**
 * Höhenmeter, die ab einer Stelle noch kommen. Mit derselben Hysterese wie im
 * Profil, sonst zählt das Rauschen mit.
 */
export function restAufstieg(profil, meter) {
  const punkte = profil?.punkte;
  if (!punkte || punkte.length < 2) return 0;
  const start = Math.max(0, Math.min(punkte[punkte.length - 1].m, meter));
  let anker = hoeheBei(punkte, start);
  if (anker === null) return 0;
  let summe = 0;
  for (const p of punkte) {
    if (p.m < start) continue;
    const d = p.h - anker;
    if (d >= SCHWELLE) { summe += d; anker = p.h; }
    else if (d <= -SCHWELLE) { anker = p.h; }
  }
  return Math.round(summe);
}

/**
 * In welchen Topf eine Kante gehoert. Die Strecke wird *aufgeteilt*, nicht
 * mehrfach gezaehlt: ein Radstreifen auf einer Hauptstrasse ist beides, und
 * wer dann "7,5 km Veloweg" und "11 km Hauptstrasse" auf einer Route von
 * 13 km liest, haelt die Zahlen zu Recht fuer falsch. Jede Kante landet in
 * genau einem Topf, vom sichersten zum unangenehmsten.
 */
function veloArt(e) {
  if (e.use === 'cycleway' || e.cycle_lane === 'separated') return 'getrennt';
  if (e.cycle_lane === 'dedicated') return 'streifen';
  if (HAUPTSTRASSEN.has(e.road_class)) return 'hauptOhne';
  return 'uebrig';
}

/** Beläge, die Valhalla als fahrbar meldet - alles andere gilt als unbefestigt. */
const FESTE_BELAEGE = new Set(['paved_smooth', 'paved', 'compacted']);
/** Strassenklassen mit Durchgangsverkehr. */
const HAUPTSTRASSEN = new Set(['motorway', 'trunk', 'primary', 'secondary']);

/**
 * Woraus die Route besteht.
 *
 * Länge und Dauer sagen nichts darüber, ob man neben Lastwagen fährt oder auf
 * einem Veloweg. `trace_attributes` wertet dieselbe Linie nochmals aus und
 * gibt je Kante Belag, Radstreifen, Strassenklasse und Routennetz zurück -
 * daraus lassen sich die Kilometer zusammenzählen.
 *
 * `bicycle_network` ist ein Bitfeld: alles über null heisst, dass die Kante
 * Teil einer signalisierten Veloroute ist.
 */
export async function fetchVeloAbschnitte({ shape, signal }) {
  if (!shape) return null;
  try {
    const res = await fetch(VALHALLA_TRACE, {
      method: 'POST',
      signal,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        encoded_polyline: shape,
        costing: 'bicycle',
        shape_match: 'edge_walk',
        filters: {
          attributes: ['edge.length', 'edge.surface', 'edge.cycle_lane', 'edge.use',
                       'edge.road_class', 'edge.bicycle_network',
                       'edge.begin_shape_index', 'edge.end_shape_index', 'shape'],
          action: 'include'
        }
      })
    });
    if (!res.ok) return null;
    const antwort = await res.json();
    const kanten = antwort.edges || [];
    if (!kanten.length) return null;
    const linie = antwort.shape ? decodePolyline(antwort.shape) : [];
    const summe = { getrennt: 0, streifen: 0, hauptOhne: 0, uebrig: 0, unbefestigt: 0, netz: 0, gesamt: 0 };
    const abschnitte = [];
    for (const e of kanten) {
      const km = e.length || 0;
      summe.gesamt += km;
      const art = veloArt(e);
      summe[art] += km;
      // Belag und Routennetz liegen quer dazu - sie beschreiben dieselben
      // Kilometer von einer anderen Seite und werden getrennt genannt.
      if (e.surface && !FESTE_BELAEGE.has(e.surface)) summe.unbefestigt += km;
      if (e.bicycle_network) summe.netz += km;

      // Fuer die Karte: gleichartige Kanten zu einem Stueck zusammenlegen.
      // Aufeinanderfolgende Kanten teilen sich einen Stuetzpunkt, deshalb
      // stossen die Stuecke ohne Luecke aneinander.
      if (!linie.length || !Number.isFinite(e.begin_shape_index)) continue;
      const stueck = linie.slice(e.begin_shape_index, e.end_shape_index + 1);
      if (stueck.length < 2) continue;
      const letzter = abschnitte[abschnitte.length - 1];
      if (letzter && letzter.art === art) letzter.coords.push(...stueck.slice(1));
      else abschnitte.push({ art, coords: stueck });
    }
    return { ...summe, abschnitte };
  } catch {
    return null;
  }
}

/**
 * Routenpunkt -> Haltestelle des Fahrplans.
 *
 * Der Dienst nimmt bei `connections` nur Namen und Haltestellen-IDs; auf
 * Koordinaten antwortet er mit einer leeren Liste. Deshalb wird zu jedem
 * Punkt zuerst die naechste Haltestelle gesucht - dieselbe Abfrage wie bei
 * der Abfahrtstafel.
 */
async function haltestelle(p, bezeichnung, signal) {
  if (p.station) return { id: p.station, name: p.name || '' };
  const h = await haltestelleBei({ lat: p.lat, lng: p.lng }, { signal });
  if (!h) throw new Error(`Keine Haltestelle in der Nähe von ${bezeichnung}.`);
  return { id: h.id, name: h.name, distanz: h.distanz };
}

/** Datum und Uhrzeit, wie der Fahrplan sie erwartet - in Schweizer Zeit. */
const alsDatum = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const alsZeit = (d) => `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;

/**
 * Verbindungen suchen. `zeit` ist der Zeitpunkt, `ankunft` sagt, ob er die
 * Abfahrt oder die Ankunft meint.
 */
export async function routeTransit({ from, to, zeit, ankunft = false, signal, limit = 4 }) {
  const [a, b] = await Promise.all([
    haltestelle(from, 'Start', signal),
    haltestelle(to, 'Ziel', signal)
  ]);
  const wann = zeit instanceof Date && !Number.isNaN(zeit.getTime()) ? zeit : new Date();
  const params = new URLSearchParams({
    from: a.id,
    to: b.id,
    date: alsDatum(wann),
    time: alsZeit(wann),
    isArrivalTime: ankunft ? '1' : '0',
    limit: String(limit)
  });
  const res = await fetch(`${TRANSPORT}/connections?${params}`, { signal });
  if (!res.ok) throw new Error(`Fahrplan nicht erreichbar (${res.status})`);
  const data = await res.json();
  const connections = (data.connections || []).map((c) => ({
    departure: c.from.departure,
    arrival: c.to.arrival,
    fromName: c.from.station?.name,
    toName: c.to.station?.name,
    transfers: c.transfers,
    duration: parseDuration(c.duration),
    legs: (c.sections || []).map((s) => ({
      walk: Boolean(s.walk) && !s.journey,
      line: s.journey ? `${s.journey.category || ''}${s.journey.number ? ' ' + s.journey.number : ''}`.trim() : null,
      // Ohne Leerzeichen - so steht das Kuerzel in den OSM-Linienrelationen.
      kuerzel: s.journey ? `${s.journey.category || ''}${s.journey.number || ''}`.trim() : null,
      kategorie: s.journey?.category || '',
      nummer: s.journey?.number ? String(s.journey.number) : '',
      to: s.journey?.to || s.arrival?.station?.name,
      fromName: s.departure?.station?.name,
      toName: s.arrival?.station?.name,
      departure: s.departure?.departure,
      arrival: s.arrival?.arrival,
      platform: s.departure?.platform,
      halte: stopList(s),
      coordinates: stopList(s).map((h) => [h.lng, h.lat])
    }))
  }));
  if (!connections.length) throw new Error('Keine Verbindung gefunden.');
  return { kind: 'transit', connections, von: a, nach: b };
}

/** "00d01:01:00" -> Minuten */
function parseDuration(d) {
  const m = /^(\d+)d(\d{2}):(\d{2}):(\d{2})$/.exec(d || '');
  if (!m) return null;
  return Number(m[1]) * 1440 + Number(m[2]) * 60 + Number(m[3]);
}

/**
 * Die Halte eines Abschnitts mit Namen und Ort.
 *
 * Mit den Namen laesst sich der Verlauf in OSM finden, mit den Orten wird er
 * auf das gefahrene Stueck beschnitten. Achtung: der Dienst nennt die Breite
 * `x` und die Laenge `y`.
 */
function stopList(section) {
  const list = section.journey?.passList || [section.departure, section.arrival];
  return list
    .map((p) => ({
      name: p?.station?.name || '',
      k: p?.station?.coordinate || p?.location?.coordinate
    }))
    .filter((p) => p.k && p.k.x != null && p.k.y != null)
    .map((p) => ({ name: p.name, lat: p.k.x, lng: p.k.y }));
}

/** Alle Teilstrecken einer Verbindung zu einer Linie verbinden */
export function connectionGeometry(connection) {
  return connection.legs.flatMap((l) => l.coordinates);
}
